import type {TargetAndTransition} from 'framer-motion';
import {getAssetUrl} from '@Utils/s3.util';

export enum LayerRole {
  BACKGROUND = 'background',
  TEXT = 'text',
}

interface LayerPoses {
  composed: TargetAndTransition;
  separated: TargetAndTransition;
}

const LAYER_TRANSFORM = {rotate: 14, skewX: 13, scaleY: 0.97};

const COMPOSED_POSES: Record<LayerRole, TargetAndTransition> = {
  [LayerRole.BACKGROUND]: {...LAYER_TRANSFORM, x: '0%', y: '0%', zIndex: 1},
  [LayerRole.TEXT]: {...LAYER_TRANSFORM, x: '0%', y: '0%', zIndex: 2},
};

export const LAYER_POSES: Record<LayerRole, LayerPoses> = {
  [LayerRole.BACKGROUND]: {
    composed: COMPOSED_POSES[LayerRole.BACKGROUND],
    separated: {...LAYER_TRANSFORM, x: '15%', y: '-26%', zIndex: 1},
  },
  [LayerRole.TEXT]: {
    composed: COMPOSED_POSES[LayerRole.TEXT],
    separated: {...LAYER_TRANSFORM, x: '-14%', y: '28%', zIndex: 2},
  },
};

// CodeReviewFayzanDone: composed poses are identical to LAYER_POSES — share them in one const, vary only separated
export const LOADER_LAYER_POSES: Record<LayerRole, LayerPoses> = {
  [LayerRole.BACKGROUND]: {
    composed: COMPOSED_POSES[LayerRole.BACKGROUND],
    separated: {...LAYER_TRANSFORM, x: '12%', y: '-9%', zIndex: 1},
  },
  [LayerRole.TEXT]: {
    composed: COMPOSED_POSES[LayerRole.TEXT],
    separated: {...LAYER_TRANSFORM, x: '-11%', y: '16%', zIndex: 2},
  },
};

const TO_SEPARATED_DELAY_MS = 80;
const TO_COMPOSED_DELAY_MS = 200;
const TRANSITION_DURATION_S = 1.5;

export enum LayersVisualVariant {
  PROTIP = 'protip',
  LOADER = 'loader',
}

export interface LayerTransitionTiming {
  delayMs: number;
  durationS: number;
}

export interface LayersAnimationTiming {
  toSeparated: LayerTransitionTiming;
  toComposed: LayerTransitionTiming;
}

export const DEFAULT_LAYERS_ANIMATION_TIMING: LayersAnimationTiming = {
  toSeparated: {delayMs: TO_SEPARATED_DELAY_MS, durationS: TRANSITION_DURATION_S},
  toComposed: {delayMs: TO_COMPOSED_DELAY_MS, durationS: TRANSITION_DURATION_S},
};

export interface EditableTextLayersVisualProps {
  layerImageUrls?: Record<LayerRole, string>;
  variant?: LayersVisualVariant;
  timing?: LayersAnimationTiming;
  seamless?: boolean;
}

export const DEFAULT_LAYER_IMAGE_URLS: Record<LayerRole, string> = {
  [LayerRole.BACKGROUND]: getAssetUrl('ai-chat-editable-text-layers-bg.webp'),
  [LayerRole.TEXT]: getAssetUrl('ai-chat-editable-text-layers-text.webp'),
};

export const LAYER_RENDER_ORDER: LayerRole[] = [LayerRole.BACKGROUND, LayerRole.TEXT];
